import { createFileRoute, Link } from "@tanstack/react-router";
import logoAsset from "@/assets/psy-bloom-logo.png.asset.json";
import ancaAsset from "@/assets/anca-vlad.jpeg.asset.json";
import nicolleAsset from "@/assets/nicolle-gheorghiu.jpeg.asset.json";
import { useRouteHead } from "@/hooks/use-route-head";

const routeHead = {
  meta: [
    { title: "PSY BLOOM Institute — Psihologi in Bucuresti" },
    {
      name: "description",
      content:
        "PSY BLOOM Institute reuneste doi psihologi clinicieni in Bucuresti. Doua perspective terapeutice complementare pentru claritate, echilibru si sanatate emotionala.",
    },
    { property: "og:title", content: "PSY BLOOM Institute — Psihologi in Bucuresti" },
    {
      property: "og:description",
      content:
        "Doua perspective terapeutice, acelasi spatiu construit pentru siguranta emotionala, claritate si dezvoltare personala.",
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "/" },
  ],
  links: [{ rel: "canonical", href: "/" }],
  scripts: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "MedicalBusiness",
        name: "PSY BLOOM Institute",
        description:
          "Institut de psihologie din Bucuresti dedicat sanatatii emotionale si dezvoltarii personale.",
        url: "/",
        email: "contact@psybloominstitute.ro",
        geo: { "@type": "GeoCoordinates", latitude: 44.449608, longitude: 26.119539 },
        address: { "@type": "PostalAddress", addressLocality: "Bucuresti", addressCountry: "RO" },
        employee: [
          { "@type": "Person", name: "Anca Vlad", jobTitle: "Psiholog clinician" },
          { "@type": "Person", name: "Nicolle Gheorghiu", jobTitle: "Psiholog clinician" },
        ],
      }),
    },
  ],
};

export const Route = createFileRoute("/")({
  component: Home,
});

const testimonials = [
  {
    quote:
      "Am venit cu multe intrebari despre mine, despre emotiile mele si despre relatiile din viata mea. In timp, am inceput sa inteleg mai bine ceea ce traiam si sa privesc lucrurile diferit. M-am simtit ascultata, apreciata si in siguranta, iar relatiile mele s-au schimbat in bine.",
    author: "Alina",
    age: "16 ani",
  },
  {
    quote:
      "In sedinte am gasit un spatiu in care am putut vorbi deschis, fara teama de a fi judecata. Am inceput sa inteleg tipare pe care nu le vedeam inainte si sa fiu mai blanda cu mine. Astazi ma simt mai linistita si mai impacata cu propria persoana.",
    author: "Clara",
    age: "47 ani",
  },
  {
    quote:
      "Am ajuns la terapie intr-o perioada dificila, marcata de anxietate si o despartire dureroasa. Am gasit un spatiu sigur in care am putut sa inteleg ceea ce simteam si sa-mi reconstruiesc treptat increderea in mine. Schimbarile nu au venit peste noapte, dar au fost reale.",
    author: "Veronica",
    age: "45 ani",
  },
  {
    quote:
      "La inceput am venit doar cu dorinta de a ma cunoaste mai bine. Procesul m-a ajutat sa descopar lucruri profunde despre mine, sa inteleg experiente din trecut si sa ma reconectez cu ceea ce conteaza cu adevarat. Pentru mine a insemnat claritate, crestere si echilibru.",
    author: "Cristina",
    age: "25 ani",
  },
];

function Home() {
  useRouteHead(routeHead);
  const mapSrc = `https://www.google.com/maps?q=44.449608,26.119539&z=16&output=embed`;

  return (
    <div className="min-h-screen bg-background">
      {/* HEADER */}
      <header className="w-full border-b border-border/60 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="PSY BLOOM Institute">
            <img src={logoAsset.url} alt="PSY BLOOM Institute" className="h-9 w-auto" width={180} height={36} />
          </Link>
          <nav aria-label="Navigare principala" className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#despre" className="hover:text-foreground transition-colors">Despre noi</a>
            <a href="https://blog.psybloominstitute.ro" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Blog</a>
            <div className="relative group">
              <button type="button" className="hover:text-foreground transition-colors inline-flex items-center gap-1">
                Specialisti
                <span aria-hidden="true" className="text-xs">▾</span>
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition-all">
                <div className="bg-background border border-border rounded-xl shadow-[var(--shadow-card)] py-2 min-w-[200px]">
                  <Link to="/anca-vlad" className="block px-4 py-2 hover:bg-muted hover:text-foreground transition-colors whitespace-nowrap">Anca Vlad</Link>
                  <Link to="/nicolle-gheorghiu" className="block px-4 py-2 hover:bg-muted hover:text-foreground transition-colors whitespace-nowrap">Nicolle Gheorghiu</Link>
                </div>
              </div>
            </div>
            <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
          </nav>
        </div>
      </header>


      <main>
        {/* HERO + SPECIALISTI */}
        <section id="specialisti" className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-surface to-background" aria-hidden="true" />
          <div className="mx-auto max-w-6xl px-6 pt-20 md:pt-28 pb-20 md:pb-28">
            <div className="max-w-4xl mx-auto text-center pb-8 md:pb-12">
              <h1 className="md:text-5xl leading-[1.1] mb-4 md:mb-6 text-balance lg:text-5xl text-3xl">
                Fiecare persoană ajunge în terapie cu o poveste diferită.
              </h1>
            </div>

            <div className="grid md:grid-cols-2 gap-6 lg:gap-7 max-w-[1000px] mx-auto">
              <SpecialistCard
                name="Anca Vlad"
                title="Psiholog clinician"
                image={ancaAsset.url}
                imageZoom={1.2}
                description="Lucrează cu persoane care se confruntă cu anxietate, burnout, suprasolicitare emoțională, dificultăți relaționale și perioade în care simt că s-au îndepărtat de ele însele. Accentul este pus pe claritate, autocunoaștere și reglare emoțională."
                cta="Descoperă abordarea"
                to="/anca-vlad"
              />

              <SpecialistCard
                name="Nicolle Gheorghiu"
                title="Psiholog clinician"
                image={nicolleAsset.url}
                description="Oferă sprijin persoanelor care traversează perioade de pierdere, anxietate, dificultăți emoționale, provocări relaționale sau situații specifice copilăriei și adolescenței. Procesul terapeutic este construit în jurul siguranței emoționale, înțelegerii de sine și regăsirii echilibrului interior."
                cta="Descoperă abordarea"
                to="/nicolle-gheorghiu"
              />
            </div>
          </div>
        </section>


        {/* BLOG */}
        <section className="py-20 md:py-28 bg-[#32506c]">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h2 className="text-3xl md:text-4xl mb-6 text-white">Resurse și articole</h2>
            <p className="text-base md:text-lg text-white/90 leading-relaxed mb-10">
              Pe blogul PSY BLOOM vei găsi articole scrise de Anca Vlad și Nicolle Gheorghiu despre anxietate, relații, burnout, dezvoltare personală, adolescenți, reglare emoțională și alte teme relevante pentru sănătatea emoțională.
            </p>
            <a
              href="https://blog.psybloominstitute.ro"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-slate-50 px-8 py-3.5 text-sm font-medium text-primary transition-all hover:bg-slate-100 hover:shadow-[var(--shadow-card)]"
            >
              Citește articolele publicate

            </a>
          </div>
        </section>

        {/* TESTIMONIALE */}
        <section id="testimoniale" className="py-20 md:py-28">
          <div className="mx-auto max-w-6xl px-6">
            <div className="text-center mb-14 md:mb-20">
              <h2 className="text-3xl md:text-4xl mb-4">Ce spun clientii</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
              {testimonials.map((t) => (
                <figure
                  key={t.author}
                  className="group relative rounded-3xl p-[1px] bg-gradient-to-br from-[#32506c]/25 via-[#32506c]/10 to-transparent shadow-[0_25px_60px_-25px_rgba(50,80,108,0.35)] transition-all hover:-translate-y-1 hover:shadow-[0_35px_80px_-25px_rgba(50,80,108,0.45)]"
                >
                  <div className="relative h-full bg-gradient-to-br from-card to-[#f5f3ee] rounded-3xl p-8 md:p-10 flex flex-col overflow-hidden">
                    <div className="relative flex items-center gap-2 mb-6">
                      <span className="h-px w-10 bg-[#32506c]/40" aria-hidden="true" />
                      <svg className="w-6 h-6 text-[#32506c]" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                        <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-1.1.9-2 2-2V8zm14 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-1.1.9-2 2-2V8z"/>
                      </svg>
                    </div>
                    <blockquote className="relative text-base md:text-lg leading-relaxed text-foreground/90 italic mb-7 flex-1">
                      {t.quote}
                    </blockquote>
                    <figcaption className="relative flex items-center gap-3 pt-6 border-t border-[#32506c]/15">
                      <span aria-hidden="true" className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-gradient-to-br from-[#32506c] to-[#1e3a55] text-white text-base font-medium shadow-md">{t.author.charAt(0)}</span>
                      <span className="text-sm text-muted-foreground">
                        <span className="font-semibold text-foreground block leading-tight">{t.author}</span>
                        <span className="text-xs">{t.age}</span>
                      </span>
                    </figcaption>
                  </div>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* DESPRE */}
        <section id="despre" className="py-20 md:py-28 bg-surface bg-slate-50">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h2 className="text-3xl md:text-4xl mb-8">Despre PSY BLOOM Institute</h2>
            <div className="space-y-6 text-base md:text-lg text-muted-foreground leading-relaxed">
              <p>
                PSY BLOOM Institute este un spațiu dedicat sănătății emoționale și dezvoltării personale. Credem că multe dintre dificultățile cu care oamenii se confruntă astăzi nu apar din lipsă de voință, ci din suprasolicitare, experiențe dificile, relații complexe sau lipsa unui cadru sigur în care să fie înțelese.
              </p>
            </div>
          </div>
        </section>

        {/* CONTACT / HARTA */}
        <section id="contact" className="py-20 md:py-28 bg-surface bg-white">
          <div className="mx-auto max-w-6xl px-6">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <address className="not-italic space-y-6 text-base md:text-lg">
                <div>
                  <h3 className="text-sm uppercase tracking-wider text-teal font-medium mb-2 font-sans">Adresa</h3>
                  <p className="text-foreground whitespace-pre-line">Intrarea Precupeții Vechi nr 6, etaj 2, sector 2</p>
                </div>
                <div>
                  <h3 className="text-sm uppercase tracking-wider text-teal font-medium mb-2 font-sans">Email</h3>
                  <a href="mailto:contact@psybloominstitute.ro" className="text-foreground hover:text-teal transition-colors">
                    contact@psybloominstitute.ro
                  </a>
                </div>
              </address>
              <div className="rounded-3xl overflow-hidden border border-border shadow-[var(--shadow-card)] aspect-[4/3] lg:aspect-[16/10]">
                <iframe
                  src={mapSrc}
                  title="Locatia PSY BLOOM Institute pe harta"
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

            </div>
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer className="border-t border-border bg-[#32506c]">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <nav aria-label="Navigare subsol" className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-white/80 mb-8">
            <a href="#despre" className="hover:text-white transition-colors">Despre</a>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <a href="https://blog.psybloominstitute.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a>
            <Link to="/politica-de-confidentialitate" className="hover:text-white transition-colors">Politica de confidentialitate</Link>
            <Link to="/politica-cookies" className="hover:text-white transition-colors">Politica cookies</Link>
            <Link to="/termeni-si-conditii" className="hover:text-white transition-colors">Termeni si conditii</Link>
          </nav>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/20 text-xs text-white/60">
            <p>© {new Date().getFullYear()} PSY BLOOM Institute</p>
            <p>
              Proiect creat de{" "}
              <a href="https://algoritmii.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Algoritmii.ro
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SpecialistCard({
  name,
  title,
  image,
  description,
  cta,
  to,
  imageZoom = 1,
}: {
  name: string;
  title: string;
  image: string;
  description: string;
  cta: string;
  to: string;
  imageZoom?: number;
}) {
  return (
    <article className="group bg-card rounded-2xl overflow-hidden border border-border/60 shadow-[var(--shadow-card)] transition-all duration-300 hover:shadow-[var(--shadow-card-hover)] hover:-translate-y-1 flex flex-col">
      <div className="aspect-[4/5] overflow-hidden bg-surface">
        <img
          src={image}
          alt={`${name}, ${title} la PSY BLOOM Institute`}
          loading="lazy"
          width={800}
          height={1000}
          style={{ transform: `scale(${imageZoom})`, transformOrigin: "top center" }}
          className="w-full h-full object-cover object-top transition-transform duration-500"
        />
      </div>

      <div className="p-5 md:p-6 flex flex-col flex-1">
        <h3 className="text-lg mb-1 md:text-2xl">{name}</h3>
        <p className="text-uppercase tracking-wider text-teal font-medium font-sans mb-3 text-sm">{title}</p>
        <p className="text-muted-foreground leading-relaxed mb-5 flex-1 text-base">{description}</p>
        <Link
          to={to}
          className="inline-flex items-center justify-center self-start rounded-full border border-primary/20 px-4 py-2 font-medium text-primary transition-all hover:bg-primary hover:text-primary-foreground text-base text-zinc-50 bg-[#32506c]"
        >
          {cta}
          <span aria-hidden="true" className="ml-2 transition-transform group-hover:translate-x-0.5">→</span>
        </Link>
      </div>
    </article>

  );
}
